import { useEffect, useMemo, useRef, useState } from 'react';
import { SYSTEM_COLORS } from '../../../../../components/system.colors';

const normalizeOptions = (options) =>
  options.map((option) => (option !== null && typeof option === 'object' ? option : { value: option, label: option }));

const getMultipleDisplayValue = (items, selected) => {
  if (items.length > 0 && selected.length === items.length) return 'Todos';
  if (selected.length === 0) return 'Nenhum';
  if (selected.length === 1) return selected[0].label;
  return `${selected.length} selecionados`;
};

const getTreeMultipleDisplayValue = (categories, selectedSet, getLabel) => {
  const selectedCategories = categories.filter((category) => selectedSet.has(category.categoria));
  if (categories.length > 0 && selectedCategories.length === categories.length) return 'Todos';
  if (selectedCategories.length === 0) return 'Nenhum';
  if (selectedCategories.length === 1) return getLabel(selectedCategories[0]);
  return `${selectedCategories.length} selecionados`;
};

const getPeriodDisplayValue = (categories, selectedSet) => {
  const selectedCategories = categories.filter((category) => selectedSet.has(category.categoria));
  if (selectedCategories.length === 0) return 'Nenhum';

  const countByYear = (list) => list.reduce((acc, category) => {
    const year = category.path[0];
    acc.set(year, (acc.get(year) ?? 0) + 1);
    return acc;
  }, new Map());

  const totals = countByYear(categories);
  const selectedTotals = countByYear(selectedCategories);
  const years = [...selectedTotals.keys()];

  if (years.every((year) => selectedTotals.get(year) === totals.get(year))) return years.join(' · ');
  if (selectedCategories.length === 1) return [...selectedCategories[0].path].reverse().join(' · ');
  return `${selectedCategories.length} meses`;
};

const buildTree = (categories) => {
  const root = { children: new Map() };

  categories.forEach(({ categoria, path }) => {
    let node = root;
    (path || []).forEach((name, depth) => {
      const childKey = name ?? '';
      if (!node.children.has(childKey)) {
        node.children.set(childKey, {
          name,
          depth,
          key: path.slice(0, depth + 1).join('\u0000'),
          children: new Map(),
          categoria: null,
        });
      }
      node = node.children.get(childKey);
    });
    node.categoria = categoria;
  });

  return root;
};

const getAncestorKeys = (path = []) =>
  path.slice(0, -1).map((_, index) => path.slice(0, index + 1).join('\u0000'));

const getGroupKeys = (node) =>
  [...node.children.values()].flatMap((child) => (child.children.size > 0 ? [child.key, ...getGroupKeys(child)] : []));

export const OtbSelect = ({
  label,
  title,
  options = [],
  value,
  onChange,
  icon = null,
  multiple = true,
  tree = null,
  searchable = false,
  periodMode = false,
  treeMultiple = false,
  notice = null,
  disabled = false,
  displayLevelIndexes = null,
  searchPlaceholder = 'Buscar em toda a hierarquia...',
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchSubmitId, setSearchSubmitId] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [searchCollapsedKeys, setSearchCollapsedKeys] = useState(new Set());  
  const [expandedKeys, setExpandedKeys] = useState(new Set());
  const [searchFocused, setSearchFocused] = useState(false);
  const [pinnedValues, setPinnedValues] = useState(new Set());

  const wrapperRef = useRef(null);

  const isTree = Boolean(tree);
  const isTreeMulti = isTree && treeMultiple; 
  const isSearchable = searchable && !isTree;
  const isSearchableMulti = isSearchable && multiple;

  const items = useMemo(() => normalizeOptions(options), [options]);
  const selectedSet = useMemo(() => new Set(multiple ? value ?? [] : [value]), [value, multiple]);
  const selected = useMemo(() => items.filter((item) => selectedSet.has(item.value)), [items, selectedSet]);

  const treeRoot = useMemo(() => (isTree ? buildTree(tree.categories || []) : null), [isTree, tree]);
  const treeLevels = tree?.levels ?? [];

  const selectedCategory = useMemo(
    () => (isTree ? (tree.categories || []).find((category) => category.categoria === value) ?? null : null),
    [isTree, tree, value]
  );

  const selectedCategoriesSet = useMemo(
    () => new Set(isTreeMulti ? value ?? [] : []),
    [isTreeMulti, value]
  );

  const getTreeLabel = (category) => {
    if (!category) return null;
    const indexes = displayLevelIndexes?.length ? displayLevelIndexes : [category.path.length - 1];
    return [...new Set(indexes)].map((index) => category.path[index]).filter(Boolean).join(' · ');
  };

  const displayValue = isTreeMulti
    ? (periodMode
        ? getPeriodDisplayValue(tree.categories || [], selectedCategoriesSet)
        : getTreeMultipleDisplayValue(tree.categories || [], selectedCategoriesSet, getTreeLabel))
    : isTree
      ? getTreeLabel(selectedCategory) ?? 'Selecione'
      : multiple
        ? getMultipleDisplayValue(items, selected)
        : selected[0]?.label ?? 'Selecione';

  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) setOpen(false);
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  useEffect(() => {
    if (!isTree && !isSearchable) return;
    if (open) {
      if (isTree) setExpandedKeys(new Set(periodMode ? getGroupKeys(treeRoot) : getAncestorKeys(selectedCategory?.path)));
    } else {
      setSearchTerm('');
      setSearchInput('');
    }
  }, [open]);

  useEffect(() => {
    setSearchCollapsedKeys(new Set());
  }, [searchTerm]);

  const toggleOpen = () => {
    if (!open) setPinnedValues(new Set(selectedSet));
    setOpen((prev) => !prev);
  };  

  const toggleOption = (item) => {
    if (!multiple) {
      if (item.value !== value) onChange?.(item.value);
      setOpen(false);
      return;
    }

    const next = selectedSet.has(item.value)
      ? selected.filter((current) => current.value !== item.value).map((current) => current.value)
      : items.filter((current) => current.value === item.value || selectedSet.has(current.value)).map((current) => current.value);
    onChange?.(next);
  };

  const toggleAll = () => {
    if (selected.length > 0) {
      onChange?.([]);
      setSearchInput('');
      setSearchTerm('');
      return;
    }
    onChange?.(visibleItems.map((item) => item.value));
  };

  const toggleExpand = (key) => {
    const setKeys = normalizedTerm ? setSearchCollapsedKeys : setExpandedKeys;
    setKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectCategory = (categoria) => {
    if (categoria !== value) onChange?.(categoria);
    setOpen(false);
  };

  const toggleCategory = (categoria) => {
    const next = (tree.categories || [])
      .filter((category) => (
        category.categoria === categoria
          ? !selectedCategoriesSet.has(categoria)
          : selectedCategoriesSet.has(category.categoria)
      ))
      .map((category) => category.categoria);
    onChange?.(next);
  }; 

  const normalizedTerm = searchTerm.trim().toLowerCase();

  const visibleItems = useMemo(() => {
    const filtered = isSearchable && normalizedTerm
      ? items.filter((item) => String(item.label ?? '').toLowerCase().includes(normalizedTerm))
      : items;

    if (!multiple || pinnedValues.size === 0) return filtered;

    return [
      ...filtered.filter((item) => pinnedValues.has(item.value)),
      ...filtered.filter((item) => !pinnedValues.has(item.value)),
    ];
  }, [items, isSearchable, normalizedTerm, multiple, pinnedValues]);

  const matchesSearch = (node) =>
    !normalizedTerm ||
    String(node.name ?? '').toLowerCase().includes(normalizedTerm) ||
    [...node.children.values()].some(matchesSearch);

  const nameMatches = (node) =>
    Boolean(normalizedTerm) && String(node.name ?? '').toLowerCase().includes(normalizedTerm);

  const getVisibleChildren = (node, matched) => {
    const children = [...node.children.values()];
    return matched ? children : children.filter(matchesSearch);
  };

  const getVisibleLeaves = (node, parentMatched = false) => {
    if (node.children.size === 0) return [node.categoria];
    const matched = isTreeMulti && (parentMatched || nameMatches(node));
    return getVisibleChildren(node, matched).flatMap((child) => getVisibleLeaves(child, matched));
  };

  const applySelection = (targets, shouldSelect) => {
    const targetSet = new Set(targets);
    const next = (tree.categories || [])
      .filter((category) => (
        targetSet.has(category.categoria) ? shouldSelect : selectedCategoriesSet.has(category.categoria)
      ))
      .map((category) => category.categoria);
    onChange?.(next);
  };

  const visibleLeaves = isTreeMulti && treeRoot ? getVisibleLeaves(treeRoot) : [];
  const hasVisibleSelection = visibleLeaves.some((categoria) => selectedCategoriesSet.has(categoria));
  const allVisibleSelected = visibleLeaves.length > 0 && visibleLeaves.every((categoria) => selectedCategoriesSet.has(categoria));
  const isToggleAllActive = periodMode ? allVisibleSelected : hasVisibleSelection;

useEffect(() => {
  if (!searchSubmitId || !normalizedTerm) return;

  if (isTreeMulti) {
    const visibleSet = new Set(visibleLeaves);
    onChange?.(
      (tree.categories || [])
        .filter((category) => visibleSet.has(category.categoria))
        .map((category) => category.categoria)
    );
    return;
  }

  if (isSearchableMulti) {
    onChange?.(visibleItems.map((item) => item.value));
  }
}, [searchSubmitId]);

  const toggleAllCategories = () => {
    if (isToggleAllActive) {
      onChange?.([]);
      setSearchInput('');
      setSearchTerm('');
      return;
    }
    applySelection(visibleLeaves, true);
  };

  const toggleGroup = (node, parentMatched) => {
    const leaves = getVisibleLeaves(node, parentMatched);
    const allSelected = leaves.length > 0 && leaves.every((categoria) => selectedCategoriesSet.has(categoria));
    applySelection(leaves, !allSelected);
  };

  const renderTreeNodes = (parent, parentMatched = false) =>
    getVisibleChildren(parent, parentMatched).map((node) => {
      const isLeaf = node.children.size === 0;
      const matched = isTreeMulti && (parentMatched || nameMatches(node));
      const expanded = normalizedTerm ? !searchCollapsedKeys.has(node.key) : expandedKeys.has(node.key);
      const isSelectedLeaf = isLeaf && (isTreeMulti ? selectedCategoriesSet.has(node.categoria) : node.categoria === value);
      const levelName = treeLevels[node.depth]?.levelName ?? '';

      const groupLeaves = isTreeMulti && !isLeaf ? getVisibleLeaves(node, parentMatched) : [];
      const groupSelectedCount = groupLeaves.filter((categoria) => selectedCategoriesSet.has(categoria)).length;
      const isGroupAll = groupLeaves.length > 0 && groupSelectedCount === groupLeaves.length;
      const isGroupPartial = groupSelectedCount > 0 && !isGroupAll;
      const isGroupActive = isGroupAll || isGroupPartial;

      return (
        <li key={node.key}>
          <button
            type="button"
            role={isLeaf ? 'option' : undefined}
            aria-selected={isLeaf ? isSelectedLeaf : undefined}
            aria-expanded={isLeaf ? undefined : expanded}
            onClick={() => (isLeaf
              ? (isTreeMulti ? toggleCategory(node.categoria) : selectCategory(node.categoria))
              : toggleExpand(node.key))}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              width: '100%',
              padding: '8px 16px',
              paddingLeft: 16 + node.depth * 18,
              background: isSelectedLeaf ? SYSTEM_COLORS.WHITE.LINEN : 'none',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
              color: SYSTEM_COLORS.BLACK.GUNMETAL,
              fontSize: "0.8rem",
              fontWeight: node.depth === 0 ? 700 : 400,
              textAlign: 'left',
              fontFamily: 'inherit',
            }}>
            {isLeaf ? (
              isTreeMulti ? (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    width: 18,
                    height: 18,
                    borderRadius: 4,
                    border: `2px solid ${isSelectedLeaf ? SYSTEM_COLORS.ORANGE.CINNABAR : SYSTEM_COLORS.BLUE.SLATE}`,
                    background: isSelectedLeaf ? SYSTEM_COLORS.ORANGE.CINNABAR : SYSTEM_COLORS.WHITE.WHITE,
                    boxSizing: 'border-box',
                  }}
                >
                  {isSelectedLeaf && (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={SYSTEM_COLORS.WHITE.WHITE} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  )}
                </span>
              ) : (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    border: `${isSelectedLeaf ? 2 : 1}px solid ${isSelectedLeaf ? SYSTEM_COLORS.ORANGE.CINNABAR : SYSTEM_COLORS.GRAY.GRAY_200}`,
                    background: isSelectedLeaf ? SYSTEM_COLORS.WHITE.WHITE : SYSTEM_COLORS.GRAY.ZINC_100,
                    boxSizing: 'border-box',
                  }}
                >
                  {isSelectedLeaf && (
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: SYSTEM_COLORS.ORANGE.CINNABAR,
                      }}
                    />
                  )}
                </span>
              )
            ) : (
              <>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={SYSTEM_COLORS.BLUE.SLATE}
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ flexShrink: 0, transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.15s' }}
                >
                  <path d="m6 9 6 6 6-6" />
                </svg>
                {isTreeMulti && (
                  <span
                    role="checkbox"
                    aria-checked={isGroupPartial ? 'mixed' : isGroupAll}
                    onClick={(event) => {
                      event.stopPropagation();
                      toggleGroup(node, parentMatched);
                    }}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      width: 18,
                      height: 18,
                      borderRadius: 4,
                      border: `2px solid ${isGroupActive ? SYSTEM_COLORS.ORANGE.CINNABAR : SYSTEM_COLORS.BLUE.SLATE}`,
                      background: isGroupActive ? SYSTEM_COLORS.ORANGE.CINNABAR : SYSTEM_COLORS.WHITE.WHITE,
                      boxSizing: 'border-box',
                    }}
                  >
                    {isGroupAll && (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={SYSTEM_COLORS.WHITE.WHITE} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                    )}
                    {isGroupPartial && (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={SYSTEM_COLORS.WHITE.WHITE} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12h14" />
                      </svg>
                    )}
                  </span>
                )}
              </>
            )}
            <span style={{ flex: 1 }}>{node.name || '—'}</span>
            <span
              style={{
                flexShrink: 0,
                color: SYSTEM_COLORS.BLUE.SLATE,
                fontSize: "0.65rem",
                fontWeight: 500,
                letterSpacing: 0.3,
                textTransform: 'uppercase',
              }}
            >
              {levelName}
            </span>
          </button>

          {!isLeaf && expanded && (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>{renderTreeNodes(node, matched)}</ul>
          )}
        </li>
      );
    });

  return (
    <div ref={wrapperRef} style={{ position: 'relative', display: 'inline-block', fontFamily: 'inherit' }}>
      <button
        type="button"
        disabled={disabled}
        onClick={toggleOpen}
        aria-haspopup="listbox"
        aria-expanded={open && !disabled}
        style={{
          display: 'flex',
          alignItems: 'stretch',
          border: `1px solid ${SYSTEM_COLORS.WHITE.ALMOND}`,
          borderRadius: 8,
          overflow: 'hidden',
          background: SYSTEM_COLORS.WHITE.WHITE,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          padding: 0,
          fontFamily: 'inherit',
        }}>
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 14px',
            background: SYSTEM_COLORS.WHITE.LINEN,
            color: SYSTEM_COLORS.ORANGE.CINNABAR,
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
            borderRight: `1px solid ${SYSTEM_COLORS.WHITE.ALMOND}`,
          }}
        >
          {icon === 'calendar' ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M8 2v4" />
              <path d="M16 2v4" />
              <rect width="18" height="18" x="3" y="4" rx="2" />
              <path d="M3 10h18" />
            </svg>
          ) : icon === 'truck' ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2" />
              <path d="M15 18H9" />
              <path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14" />
              <circle cx="17" cy="18" r="2" />
              <circle cx="7" cy="18" r="2" />
            </svg>
          ) : icon === 'package' || (!icon && isTree) ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m7.5 4.27 9 5.15" />
              <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
              <path d="m3.3 7 8.7 5 8.7-5" />
              <path d="M12 22V12" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2 2 7l10 5 10-5-10-5Z" />
              <path d="m2 17 10 5 10-5" />
              <path d="m2 12 10 5 10-5" />
            </svg>
          )}
          {label}
        </span>
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 14px',
            color: SYSTEM_COLORS.BLACK.GUNMETAL,
            fontSize: 15,
            fontWeight: 700,
            whiteSpace: 'nowrap',
          }}
        >
          {displayValue}
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ transform: open ? 'rotate(0deg)' : 'rotate(180deg)', transition: 'transform 0.15s' }}
          >
            <path d="m18 15-6-6-6 6" />
          </svg>
        </span>
      </button>

      {open && !disabled && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            ...(periodMode ? { right: 0, minWidth: 300 } : { left: 0, minWidth: isTree ? 420 : 260 }),
            background: SYSTEM_COLORS.WHITE.WHITE,
            borderRadius: 12,
            boxShadow: `0 8px 24px ${SYSTEM_COLORS.BLACK.DARK_NAVY_12}`,
            zIndex: 1000,
            overflow: 'hidden',
          }}>
          <div
            style={{
              display: 'flex', alignItems: 'center',
              justifyContent: 'space-between', gap: 16,
              padding: '14px 16px',
              borderBottom: `1px solid ${SYSTEM_COLORS.GRAY.ANTI_FLASH}` }}>
            <span
              style={{ color: SYSTEM_COLORS.BLUE.SLATE, fontSize: "0.75rem", fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}>
              {isTree && !periodMode
                ? [title ?? label, treeLevels.map((level) => level.levelName).filter(Boolean).join(' → ')].filter(Boolean).join(' · ')
                : title ?? label}
            </span>

            {(isTreeMulti || (multiple && !isTree)) && (
              <button
                type="button"
                onClick={isTreeMulti ? toggleAllCategories : toggleAll}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  color: SYSTEM_COLORS.ORANGE.CINNABAR,
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}>
                {(isTreeMulti ? isToggleAllActive : selected.length > 0) ? 'Desmarcar todos' : 'Marcar todos'}
              </button>
            )}
          </div>

          {notice && (
            <div
              style={{
                padding: '8px 16px',
                background: SYSTEM_COLORS.WHITE.LINEN,
                borderBottom: `1px solid ${SYSTEM_COLORS.WHITE.ALMOND}`,
                color: SYSTEM_COLORS.BLUE.SLATE,
                fontSize: '0.7rem',
                fontWeight: 500,
              }}
            >
              {notice}
            </div>
          )}          

          {isTree && selectedCategory && (
            <div
              style={{
                display: 'flex', alignItems: 'end', gap: 8, padding: '12px 16px',
                background: SYSTEM_COLORS.WHITE.LINEN, borderBottom: `1px solid ${SYSTEM_COLORS.WHITE.ALMOND}` }}>

              <span
                style={{ color: SYSTEM_COLORS.BLUE.SLATE, fontSize: "0.75rem", fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                Selecionado:
              </span>
              
              <span
                style={{
                  flex: 1,
                  color: SYSTEM_COLORS.ORANGE.CINNABAR,
                  fontSize: "0.8rem",
                  fontWeight: 700,
                  textTransform: 'uppercase',
                }}>

                {getTreeLabel(selectedCategory)}
              </span>

              <button
                type="button"
                onClick={() => onChange?.(null)}
                aria-label="Limpar seleção"
                style={{
                  display: 'inline-flex',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  color: SYSTEM_COLORS.BLUE.SLATE,
                }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </svg>
              </button>
            </div>
          )}

          {(isTree || isSearchable) && !periodMode && (
            <div style={{ padding: '12px 16px', borderBottom: `1px solid ${SYSTEM_COLORS.GRAY.ANTI_FLASH}` }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  border: `1px solid ${isSearchable && searchFocused ? SYSTEM_COLORS.ORANGE.CINNABAR : SYSTEM_COLORS.WHITE.ALMOND}`,
                  boxShadow: isSearchable && searchFocused ? `0 0 0 3px ${SYSTEM_COLORS.WHITE.LINEN}` : 'none',
                  borderRadius: 8,
                  padding: '8px 12px',
                }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={SYSTEM_COLORS.BLUE.SLATE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.3-4.3" />
                </svg>

                <input
                  value={searchInput}
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    setSearchInput(nextValue);
                    setSearchTerm(nextValue);
                    if (!(isTreeMulti || isSearchableMulti) || !nextValue.trim()) setSearchTerm(nextValue);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      setSearchTerm(searchInput);
                      setSearchSubmitId((prev) => prev + 1);
                    }
                  }}
                  placeholder={searchPlaceholder}
                  autoFocus
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setSearchFocused(false)}                  
                  style={{
                    flex: 1,
                    border: 'none',
                    outline: 'none',
                    background: 'none',
                    color: SYSTEM_COLORS.BLACK.GUNMETAL,
                    fontSize: "0.8rem",
                    fontFamily: 'inherit',
                  }}
                />
              </div>
            </div>
          )}

          {isTree ? (
            <ul
              role="listbox"
              aria-multiselectable={isTreeMulti || undefined}              
              style={{ listStyle: 'none', margin: 0, padding: '6px 0', maxHeight: 360, overflowY: 'auto' }}>
              {treeRoot && renderTreeNodes(treeRoot)}
            </ul>
          ) : (
            <ul
              role="listbox"
              aria-multiselectable={multiple}
              style={{ listStyle: 'none', margin: 0, padding: '6px 0', maxHeight: 320, overflowY: 'auto' }}
            >
              {visibleItems.map((item) => {
                const checked = selectedSet.has(item.value);
                return (
                  <li key={item.value}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={checked}
                      onClick={() => toggleOption(item)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        width: '100%',
                        padding: '9px 16px',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: SYSTEM_COLORS.BLACK.GUNMETAL,
                        fontSize: "0.75rem",
                        fontWeight: (multiple && !isSearchable) || checked ? 600 : 400,
                        textAlign: 'left',
                        fontFamily: 'inherit',
                      }}
                    >
                      {multiple ? (
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            width: 18,
                            height: 18,
                            borderRadius: 4,
                            border: `2px solid ${checked ? SYSTEM_COLORS.ORANGE.CINNABAR : SYSTEM_COLORS.BLUE.SLATE}`,
                            background: checked ? SYSTEM_COLORS.ORANGE.CINNABAR : SYSTEM_COLORS.WHITE.WHITE,
                            boxSizing: 'border-box',
                          }}
                        >
                          {checked && (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={SYSTEM_COLORS.WHITE.WHITE} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M20 6 9 17l-5-5" />
                            </svg>
                          )}
                        </span>
                      ) : (
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            width: 18,
                            height: 18,
                            borderRadius: '50%',
                            border: `${checked ? 2 : 1}px solid ${checked ? SYSTEM_COLORS.ORANGE.CINNABAR : SYSTEM_COLORS.GRAY.GRAY_200}`,
                            background: checked ? SYSTEM_COLORS.WHITE.WHITE : SYSTEM_COLORS.GRAY.ZINC_100,
                            boxSizing: 'border-box',
                          }}
                        >
                          {checked && (
                            <span
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                background: SYSTEM_COLORS.ORANGE.CINNABAR,
                              }}
                            />
                          )}
                        </span>
                      )}
                      {item.label}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};